import { useStore } from '@/store'
import { onlyUnique } from '@acme/common'
import { invariant } from '@acme/core'
import { ExpandedState } from '@tanstack/react-table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IDataTableProps } from '../components/DataTable'
import normalizePageSize from '../components/DataTable/normalizePageSize'
import FilterModelPro, { GridFilterModelPro } from '../utils/filterModelPro'
import {
    GridColDefPro,
    GridSelectionModel,
    TDensity,
    ValidRowModel,
} from '../utils/types'
import useDefaultFilterModel from './useDefaultFilterModel'
import useDefaultSelectionModel from './useDefaultSelectionModel'

export const optionsColumn: Partial<GridColDefPro> = {
    disableColumnMenu: true,
    disableExport: true,
    filterable: false,
    sortable: false,
    hideable: false,
    headerName: '',
    width: 60,
    align: 'center',
}

const SERVER_FILTER_DEBOUNCE_MS = 300

type OptionalFilterModelProps =
    | {
          controlledFilterModel: ReturnType<typeof useDefaultFilterModel>[0]
          setFilterModel: ReturnType<typeof useDefaultFilterModel>[1]
      }
    | {
          controlledFilterModel?: never
          setFilterModel?: never
      }

type OptionalSelectionModelProps = {
    selectionModel?: ReturnType<typeof useDefaultSelectionModel>[0]
    onSelectionModelChange?: ReturnType<typeof useDefaultSelectionModel>[1]
}

interface UseDataGridProProps<T extends ValidRowModel> {
    density?: IDataTableProps<T>['density']
    rows?: IDataTableProps<T>['rows']
    columns: IDataTableProps<T>['columns']
    initialFilterModel?: IDataTableProps<T>['filterModel']
    defaultPageSize: NonNullable<IDataTableProps<T>['defaultPageSize']>
    pageSize: IDataTableProps<T>['pageSize']
    isLoading?: IDataTableProps<T>['loading']
    onRowClick?: IDataTableProps<T>['onRowClick']
    onCellEditCommit?: IDataTableProps<T>['onCellEditCommit']
    checkboxSelection?: IDataTableProps<T>['checkboxSelection']
    columnVisibilityModel?: IDataTableProps<T>['columnVisibilityModel']
    getRowClassName?: IDataTableProps<T>['getRowClassName']
    sortModel?: IDataTableProps<T>['sortModel']
    onSortModelChange?: IDataTableProps<T>['onSortModelChange']
    enableRowExpanding?: IDataTableProps<T>['enableRowExpanding']
    columnGroupingModel?: IDataTableProps<T>['columnGroupingModel']
    pageSizeOptions?: IDataTableProps<T>['pageSizeOptions']
    getRowsToExport?: IDataTableProps<T>['getRowsToExport']
    totalRows?: IDataTableProps<T>['totalRows']
    onPaginationChange?: IDataTableProps<T>['onPaginationChange']
    /** Chamado quando o filtro muda em modo server-side */
    onFilterChange?: (filterModel: GridFilterModelPro) => void
    heightControls?: {
        rowHeight?: IDataTableProps<T>['rowHeight']
        headerHeight?: IDataTableProps<T>['headerHeight']
        getRowHeight?: IDataTableProps<T>['getRowHeight']
    }
    isServerSide?: boolean
}

type TProps<T extends ValidRowModel> = UseDataGridProProps<T> &
    OptionalFilterModelProps &
    OptionalSelectionModelProps

const useDataGridPro = <T extends ValidRowModel>({
    density: initialDensity = 'standard',
    rows = [],
    columns,
    initialFilterModel,
    controlledFilterModel,
    setFilterModel,
    defaultPageSize,
    pageSize: fixedPageSize,
    isLoading = false,
    onSelectionModelChange,
    selectionModel,
    checkboxSelection = false,
    columnVisibilityModel = {},
    heightControls = {},
    enableRowExpanding = false,
    totalRows,
    onPaginationChange,
    onFilterChange,
    isServerSide = false,
    ...rest
}: TProps<T>) => {
    const [_filterModel, _onFilterModelChange] = useDefaultFilterModel(
        initialFilterModel,
        !!controlledFilterModel,
    )
    const [_selectionModel, _setSelecionModel] = useDefaultSelectionModel()
    const filterModel = controlledFilterModel ?? _filterModel,
        onFilterModelChange = setFilterModel ?? _onFilterModelChange
    const filterModelRef = useRef(filterModel)
    const [isFiltroVisivel, setFiltroVisivel] = useState<boolean>(false)
    const [visibilidadeColunas, setVisibilidadeColunas] =
        useState<boolean>(false)
    const [colunas, setColunas] = useState<Array<GridColDefPro>>(columns)
    const [density, setDensity] = useState<TDensity>(initialDensity)
    const [expanded, setExpanded] = useState<ExpandedState>({})

    const { pageSize = defaultPageSize, setPageSize } = useStore(
        state => state.dataTable.pagination,
    )

    const [pagination, setPaginationState] = useState<{
        pageIndex: number
        pageSize: number
    }>({
        pageIndex: 0,
        pageSize:
            normalizePageSize(fixedPageSize) ?? normalizePageSize(pageSize),
    })
    const serverFilterTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        filterModelRef.current = filterModel
    }, [filterModel])

    useEffect(() => {
        return () => {
            if (serverFilterTimeoutRef.current) {
                clearTimeout(serverFilterTimeoutRef.current)
            }
        }
    }, [])

    const setPagination = useCallback(
        (
            updater: React.SetStateAction<{
                pageIndex: number
                pageSize: number
            }>,
        ) => {
            setPaginationState(prev => {
                const next =
                    typeof updater === 'function' ? updater(prev) : updater
                if (isServerSide) onPaginationChange?.(next)
                return next
            })
        },
        [isServerSide, onPaginationChange],
    )

    const gridData = useMemo(() => {
        if (isServerSide) return rows
        return rows.filter(new FilterModelPro(filterModel, columns).filter)
    }, [rows, filterModel, isServerSide])

    // Reseta a paginação (apenas client-side)
    useEffect(() => {
        if (isServerSide) return
        if (
            pagination.pageIndex >
            Math.ceil(gridData.length / pagination.pageSize) - 1
        ) {
            setPagination(prev => ({ ...prev, pageIndex: 0 }))
        }
    }, [gridData?.length, pagination.pageIndex, isServerSide])

    const cols = useMemo(
        () =>
            colunas.map(column => {
                if (Object.keys(columnVisibilityModel).includes(column.field))
                    column.hide = !columnVisibilityModel[column.field]
                return column
            }),
        [colunas, columnVisibilityModel],
    )

    function setSelectionModel(
        valueOrUpdater: React.SetStateAction<GridSelectionModel>,
    ) {
        onSelectionModelChange?.(valueOrUpdater)
        _setSelecionModel(valueOrUpdater)
    }

    // if gridData does not contains ids from selectionModel, remove it from selectionModel
    useEffect(() => {
        if (selectionModel && selectionModel.length > 0) {
            const ids = gridData.map(row => row.id)
            const newSelectionModel = selectionModel.filter(id =>
                ids.includes(id),
            )
            if (newSelectionModel.length !== selectionModel.length) {
                setSelectionModel(newSelectionModel)
            }
        }
    }, [gridData])

    invariant(
        gridData.map(it => String(it.id)).filter(onlyUnique).length ===
            gridData.length,
        'Cada linha deve ter um ID único',
    )

    const onFilterModelChangePro = useCallback(
        (updater: React.SetStateAction<GridFilterModelPro>) => {
            onFilterModelChange(updater)
            if (isServerSide) {
                const newFilter =
                    typeof updater === 'function'
                        ? updater(filterModelRef.current)
                        : updater
                filterModelRef.current = newFilter
                setPaginationState(prev => ({ ...prev, pageIndex: 0 }))

                if (serverFilterTimeoutRef.current) {
                    clearTimeout(serverFilterTimeoutRef.current)
                }

                serverFilterTimeoutRef.current = setTimeout(() => {
                    setPaginationState(prev => {
                        const next = { ...prev, pageIndex: 0 }
                        onPaginationChange?.(next)
                        return next
                    })
                    onFilterChange?.(newFilter)
                }, SERVER_FILTER_DEBOUNCE_MS)
            }
        },
        [onFilterModelChange, isServerSide, onFilterChange, onPaginationChange],
    )

    return {
        rows: gridData,
        filterLength: filterModel.items.length,
        columns: cols,
        filterModelPro: filterModel,
        onFilterModelChangePro,
        isFiltroVisivel,
        setFiltroVisivel,
        density,
        setDensity,
        visibilidadeColunas,
        setVisibilidadeColunas,
        setColunas,
        setPageSize: (value: number) => {
            setPageSize(value)
            setPagination(prev => ({
                ...prev,
                pageSize: value,
                pageIndex: isServerSide ? 0 : prev.pageIndex,
            }))
        },
        setPagination,
        pagination,
        totalRows,
        isLoading,
        selectionModel: selectionModel ?? _selectionModel,
        setSelectionModel,
        checkboxSelection,
        heightControls,
        expanded,
        onExpandedChange: setExpanded,
        enableRowExpanding,
        ...rest,
    }
}

export default useDataGridPro
